import { createSlice } from '@reduxjs/toolkit';
import {  useSelector } from "react-redux";
import {  useMemo } from "react";
import {
    addAuthentication,
    getAuthentications,
} from '../localStorage/localStorageAuthenticationApi';
import { UserInfo } from "../auth/model/UserInfo";
import { getLocalStorage } from "../localStorage/createPrefixedLocalStorage";

export type AuthEntry =
    | {
          key: string;
          rawToken: null;
          isLogin: false;
          currentUser: undefined;
      }
    | {
          key: string;
          rawToken: string;
          isLogin: undefined; // token presente, ma non validato
          currentUser: UserInfo | undefined;
      }
    | {
          key: string;
          rawToken: string;
          isLogin: true; // token validato
          currentUser: UserInfo;
      }
    | {
          key: string;
          rawToken: string;
          isLogin: true; // token validato
          currentUser: undefined;
      };



export type AuthState = {
    authentications: AuthEntry[];
};
export const initialCurrentUser = undefined

export const getExistingRawToken = (key: string) => {
    const prefixedStorage = getLocalStorage();
    return prefixedStorage.getItem(key);
};

export const getInitialCurrentUser = (key: string): UserInfo | null => {
    const prefixedStorage = getLocalStorage()
    const userInfo = prefixedStorage.getItem("user-info")
    if (userInfo !== null) {
        return JSON.parse(userInfo) as UserInfo
    }
    return null
}

export const getInitialTokenState = (key: string): AuthEntry => {
    const existingRawToken = getExistingRawToken(key);
    if (existingRawToken === null) {
        return {
            key: key,
            isLogin: false,
            rawToken: existingRawToken,
            currentUser: undefined,
        };
    }
    const existingCurrentUser: UserInfo | undefined =
        getInitialCurrentUser(key) !== null
            ? (getInitialCurrentUser(key) as UserInfo)
            : initialCurrentUser;
    return {
        key: key,
        isLogin: undefined,
        currentUser: existingCurrentUser
            ? existingCurrentUser
            : initialCurrentUser,
        rawToken: existingRawToken,
    };
};

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        authentications: Array.from(getAuthentications()).map(auth => getInitialTokenState(auth)),
    },
    reducers: {
        setCurrentUser(state, action: { type: string, payload: { currentUser: UserInfo, key: string} }) {
            const index = getTokenByKey(action.payload.key, state.authentications)
            if (index !== -1) {
                state.authentications[index].currentUser = action.payload.currentUser
                state.authentications[index].isLogin = true

                state.authentications = [...state.authentications]
            } else {
                throw new Error("Non è possibile!")

            }
        },
        createAuth(state, action: { type: string, payload: {key: string, rawToken: string}}){
            const index = getTokenByKey(action.payload.key, state.authentications);
            if (index !== -1) {
                state.authentications[index].rawToken = action.payload.rawToken
                addAuthentication(
                    action.payload.key,
                    JSON.stringify(action.payload.rawToken),
                );
            } else {
                state.authentications = [...state.authentications, {key: action.payload.key, rawToken: action.payload.rawToken, isLogin: undefined, currentUser: undefined}]
                addAuthentication(action.payload.key, JSON.stringify(action.payload.rawToken));
            }
        },

        setLogin(state, action: { type: string, payload: { key: string , login: boolean} }) {
            const index = getTokenByKey(action.payload.key, state.authentications)
            if (index !== -1) {
                state.authentications[index].isLogin = action.payload.login
            }
            state.authentications = [...state.authentications];
        }
    },
});

const getTokenByKey = (key: string, state: any[]) => {
    return state.findIndex(element => element.key === key);
}

export const useGetToken: (key: string) => AuthEntry = (key: string) => {
    const auth = useSelector((state: any) => state.auth) as AuthState;

    return useMemo(() => {
        const currentAuth: AuthEntry | undefined = auth.authentications.find((elem: AuthEntry) => {
            return elem.key === key
        })
        return currentAuth ?? ({ isLogin: false, currentUser: undefined, key:key, rawToken: null })
    }, [auth, key]);
}

export const { setCurrentUser, setLogin, createAuth } = authSlice.actions;
const authReducer = authSlice.reducer;

export default authReducer;
